import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { NavA } from "./Nav";
import { readConsent, restoreConsent, saveConsent } from "@/lib/consent";
import { useT } from "@/i18n";

const btn = "inline-flex h-11 items-center justify-center rounded-full px-5 font-mono text-xs uppercase tracking-[0.14em] transition-colors duration-300";
const solid = `${btn} bg-[#F7F5F0] text-[#121110] hover:bg-[#D98A52]`;
const ghost = `${btn} border border-white/25 text-[#F7F5F0] hover:border-[#D98A52] hover:text-[#D98A52]`;

const Toggle = ({ id, label, desc, checked, onChange, locked }) => (
  <label className="flex items-start justify-between gap-4 border-t border-white/10 py-3" data-testid={`cookie-option-${id}`}>
    <span>
      <span className="block text-sm font-semibold">{label}</span>
      <span className="mt-0.5 block text-xs leading-relaxed text-[#C9C3B8]">{desc}</span>
    </span>
    <input type="checkbox" checked={checked} disabled={locked} onChange={(e) => onChange?.(e.target.checked)}
      className="mt-1 h-5 w-5 shrink-0 accent-[#D98A52]" data-testid={`cookie-toggle-${id}`} />
  </label>
);

const Options = ({ t, choice, setChoice }) => (
  <div className="mt-4" data-testid="cookie-options">
    <Toggle id="necessary" label={t("ck.nec", "Necessari")} desc={t("ck.necD", "Lingua scelta e dati dei moduli salvati nel tuo browser. Servono al funzionamento del sito e non si possono disattivare.")} checked locked />
    <Toggle id="stats" label={t("ck.stats", "Statistiche")} desc={t("ck.statsD", "Ci aiutano a capire quali pagine vengono consultate e come migliorarle (PostHog).")} checked={choice.stats} onChange={(v) => setChoice({ ...choice, stats: v })} />
    <Toggle id="maps" label={t("ck.maps", "Mappe")} desc={t("ck.mapsD", "Mostrano le mappe Google dei nostri stabilimenti; Google può impostare i propri cookie.")} checked={choice.maps} onChange={(v) => setChoice({ ...choice, maps: v })} />
  </div>
);

export const CookieBanner = () => {
  const t = useT();
  const [open, setOpen] = useState(() => !readConsent());
  const [custom, setCustom] = useState(false);
  const [choice, setChoice] = useState(() => readConsent() ?? { stats: false, maps: false });
  useEffect(() => {
    restoreConsent();
    const reopen = () => { setChoice(readConsent() ?? { stats: false, maps: false }); setCustom(true); setOpen(true); };
    window.addEventListener("open-cookie-settings", reopen);
    return () => window.removeEventListener("open-cookie-settings", reopen);
  }, []);
  if (!open) return null;
  const done = (c) => { saveConsent(c); setOpen(false); setCustom(false); };
  return (
    <div role="dialog" aria-label={t("ck.title", "Cookie e privacy")} className="fixed inset-x-3 bottom-3 z-[90] mx-auto max-w-xl rounded-2xl border border-white/10 bg-[#121110]/95 p-5 text-[#F7F5F0] shadow-2xl backdrop-blur-xl sm:inset-x-auto sm:left-6 sm:p-6" data-testid="cookie-banner">
      <button onClick={() => done({ stats: false, maps: false })} aria-label={t("ck.closeReject", "Chiudi e rifiuta i cookie facoltativi")} className="absolute right-3 top-3 grid h-11 w-11 place-items-center rounded-full text-[#C9C3B8] hover:text-[#F7F5F0]" data-testid="cookie-close">
        <X className="h-4 w-4" />
      </button>
      <p className="pr-10 font-display text-lg font-bold">{t("ck.title", "Cookie e privacy")}</p>
      <p className="mt-2 text-sm leading-relaxed text-[#D6D0C4]">
        {t("ck.text", "Usiamo cookie tecnici necessari e, solo con il tuo consenso, cookie di statistica e le mappe di Google. Puoi cambiare scelta quando vuoi da \u201cPreferenze cookie\u201d nel pi\u00e8 di pagina.")}{" "}
        <NavA to="/cookie-policy" onGo={() => setOpen(false)} className="underline underline-offset-2 hover:text-[#D98A52]" data-testid="cookie-policy-link">{t("ck.policy", "Cookie policy")}</NavA>
      </p>
      {custom && <Options t={t} choice={choice} setChoice={setChoice} />}
      <div className="mt-5 grid gap-2 sm:grid-cols-3">
        <button onClick={() => done({ stats: false, maps: false })} className={ghost} data-testid="cookie-reject">{t("ck.reject", "Rifiuta")}</button>
        {custom
          ? <button onClick={() => done(choice)} className={ghost} data-testid="cookie-save">{t("ck.save", "Salva scelte")}</button>
          : <button onClick={() => setCustom(true)} className={ghost} data-testid="cookie-customize">{t("ck.custom", "Personalizza")}</button>}
        <button onClick={() => done({ stats: true, maps: true })} className={solid} data-testid="cookie-accept">{t("ck.accept", "Accetta tutti")}</button>
      </div>
    </div>
  );
};
